import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { EscopoFeriado, Feriado } from '@sgc/contracts';
import { UFS } from '@sgc/contracts';
import { del, get, post } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Campo, Sel } from '../ui/campos';
import { Modal, Pill, Sec } from '../ui/layout';
import { useToast } from '../ui/toast';

export const Route = createFileRoute('/_app/feriados')({ component: Feriados });

const ESCOPOS: ReadonlyArray<readonly [EscopoFeriado, string]> = [['MUNICIPIO', 'Municipal'], ['UF', 'Estadual'], ['NACIONAL', 'Nacional (decretado)'], ['BANCARIO', 'Bancário']];

function Feriados() {
  const qc = useQueryClient();
  const toast = useToast();
  const { eu } = useAuth();
  const pode = eu?.escritorio?.papel === 'ADMIN' || eu?.escritorio?.papel === 'SUPERVISOR';
  const q = useQuery({ queryKey: ['feriados'], queryFn: () => get<Feriado[]>('/v1/feriados') });
  const [novo, setNovo] = useState<{ escopo: EscopoFeriado; uf: string; ibge: string; municipio: string; dia: string; mes: string; ano: string; nome: string } | null>(null);

  const criar = useMutation({
    mutationFn: () => post('/v1/feriados', {
      escopo: novo!.escopo, uf: novo!.escopo === 'UF' ? novo!.uf : null, municipioIbge: novo!.escopo === 'MUNICIPIO' ? Number(novo!.ibge) : null,
      municipioNome: novo!.escopo === 'MUNICIPIO' ? novo!.municipio : null, dia: Number(novo!.dia), mes: Number(novo!.mes), ano: novo!.ano ? Number(novo!.ano) : null, nome: novo!.nome,
    }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['feriados'] }); setNovo(null); toast.ok('feriado cadastrado — as tarefas abertas são recalculadas no próximo ciclo'); },
    onError: (e) => toast.erro(e),
  });
  const excluir = useMutation({
    mutationFn: (id: string) => del(`/v1/feriados/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['feriados'] }); toast.ok('feriado removido'); },
    onError: (e) => toast.erro(e),
  });

  return (
    <>
      <div className="topbar"><div><h1>Feriados</h1><div className="sub">nacionais por lei e móveis (Sexta Santa, Carnaval, Corpus Christi) são automáticos — aqui entram estaduais, municipais e decretados</div></div></div>
      <div className="conteudo">
        <Sec titulo="Cadastrados" sub={`${q.data?.length ?? 0} registro(s)`} acoes={pode && <button className="btn mini primario" type="button" onClick={() => setNovo({ escopo: 'MUNICIPIO', uf: 'SP', ibge: '', municipio: '', dia: '', mes: '', ano: '', nome: '' })}>+ Novo feriado</button>}>
          <div className="tbwrap">
            <table className="tb">
              <thead><tr><th>Escopo</th><th>UF / município</th><th>Data</th><th>Nome</th><th>Origem</th><th /></tr></thead>
              <tbody>
                {q.data?.map((f) => (
                  <tr key={f.id}>
                    <td>{ESCOPOS.find(([v]) => v === f.escopo)?.[1]}</td>
                    <td>{f.uf ?? ''}{f.municipioNome ? ` ${f.municipioNome} (${f.municipioIbge})` : ''}</td>
                    <td className="mono">{String(f.dia).padStart(2, '0')}/{String(f.mes).padStart(2, '0')}{f.ano ? `/${f.ano}` : <span className="dim"> todo ano</span>}</td>
                    <td>{f.nome}</td>
                    <td>{f.escritorioId ? <Pill cor="acento">do escritório</Pill> : <Pill>catálogo</Pill>}</td>
                    <td className="acoes">{pode && f.escritorioId && <button className="btn mini perigo" type="button" onClick={() => { if (confirm(`Remover ${f.nome}?`)) excluir.mutate(f.id); }}>Remover</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Sec>
        <Modal titulo="Novo feriado" aberto={!!novo} onFechar={() => setNovo(null)}
          acoes={<><button className="btn" type="button" onClick={() => setNovo(null)}>Cancelar</button><button className="btn primario" type="button" disabled={criar.isPending || !novo?.nome || !novo.dia || !novo.mes} onClick={() => criar.mutate()}>Salvar</button></>}>
          {novo && (
            <div className="fgrid">
              <Sel className="c4" rotulo="Escopo" valor={novo.escopo} opcoes={ESCOPOS} onChange={(v) => setNovo({ ...novo, escopo: v as EscopoFeriado })} />
              {novo.escopo === 'UF' && <Sel className="c2" rotulo="UF" valor={novo.uf} opcoes={UFS.map((u) => [u, u] as const)} onChange={(v) => setNovo({ ...novo, uf: v })} />}
              {novo.escopo === 'MUNICIPIO' && <>
                <Campo className="c3" rotulo="Código IBGE" valor={novo.ibge} onChange={(v) => setNovo({ ...novo, ibge: v.replace(/\D/g, '') })} inputMode="numeric" obrigatorio ajuda="7 dígitos; o mesmo do cadastro do cliente" />
                <Campo className="c5" rotulo="Município" valor={novo.municipio} onChange={(v) => setNovo({ ...novo, municipio: v })} obrigatorio />
              </>}
              <Campo className="c2" rotulo="Dia" valor={novo.dia} onChange={(v) => setNovo({ ...novo, dia: v.replace(/\D/g, '').slice(0, 2) })} inputMode="numeric" obrigatorio />
              <Campo className="c2" rotulo="Mês" valor={novo.mes} onChange={(v) => setNovo({ ...novo, mes: v.replace(/\D/g, '').slice(0, 2) })} inputMode="numeric" obrigatorio />
              <Campo className="c3" rotulo="Ano" valor={novo.ano} onChange={(v) => setNovo({ ...novo, ano: v.replace(/\D/g, '').slice(0, 4) })} inputMode="numeric" ajuda="vazio = todo ano" />
              <Campo className="c5" rotulo="Nome" valor={novo.nome} onChange={(v) => setNovo({ ...novo, nome: v })} obrigatorio />
            </div>
          )}
        </Modal>
      </div>
    </>
  );
}
